import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Vendor } from '../../types/vendors';
import { deleteVendor } from '../../features/vendors/vendorSlice';
import EditVendorForm from './EditVendorForm';
import Card from '../ui/Card';
import Button from '../ui/Button';
import ErrorMessage from '../ui/ErrorMessage';
import { Mail, Phone, MapPin } from 'react-feather';
import { formatCurrency } from '../../utils/formatters';

interface Props {
  vendor: Vendor;
}

const VendorCard: React.FC<Props> = ({ vendor }) => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<{ message: string; sales?: any[] } | null>(null);

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this vendor?')) {
      try {
        const result = await dispatch(deleteVendor(vendor.id));
        if (deleteVendor.rejected.match(result)) {
          try {
            const errorData = JSON.parse(result.payload as string);
            setError(errorData);
          } catch {
            setError({ message: result.payload as string });
          }
        }
      } catch (err) {
        setError({ message: 'Failed to delete vendor' });
      }
    }
  };

  if (isEditing) {
    return (
      <EditVendorForm
        vendor={vendor}
        onCancel={() => setIsEditing(false)}
        onSuccess={() => setIsEditing(false)}
      />
    );
  }

  return (
    <Card>
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{vendor.name}</h3>
          {vendor.contact_person && (
            <p className="text-sm text-gray-600">Contact: {vendor.contact_person}</p>
          )}
        </div>

        <div className="space-y-2">
          {vendor.email && (
            <div className="flex items-center text-sm text-gray-600">
              <Mail className="h-4 w-4 mr-2" />
              <a href={`mailto:${vendor.email}`} className="hover:text-green-600">
                {vendor.email}
              </a>
            </div>
          )}
          
          {vendor.phone && (
            <div className="flex items-center text-sm text-gray-600">
              <Phone className="h-4 w-4 mr-2" />
              <a href={`tel:${vendor.phone}`} className="hover:text-green-600">
                {vendor.phone}
              </a>
            </div>
          )}
          
          {vendor.address && (
            <div className="flex items-start text-sm text-gray-600">
              <MapPin className="h-4 w-4 mr-2 mt-1" />
              <span>{vendor.address}</span>
            </div>
          )}
        </div>

        {error && (
          <div className="space-y-2">
            <ErrorMessage message={error.message} />
            {error.sales && (
              <div className="mt-2 text-sm">
                <p className="font-medium">Associated Sales:</p>
                <ul className="mt-1 space-y-1">
                  {error.sales.map((sale) => (
                    <li key={sale.id} className="text-gray-600">
                      {sale.date} - {formatCurrency(sale.amount)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end space-x-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
          >
            Delete
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default VendorCard;